import React, { useState } from 'react';
import { cn } from '@/shared/lib/utils';
import { toast } from 'sonner';
import type { IssueAssignee, TaskIssue } from '@momentum/types';
import { IssueStatus, RecurrencePattern } from '@momentum/types';
import type { RecurrenceConfigUnion } from '@momentum/types';
import { TaskAssignees } from '@/features/goals/hierarchy-tree/tasks-table/components/TaskAssignees';
import { useDateFormatter } from '@/shared/hooks/useDateFormatter';
import { useUpdateTaskStatus, useDeleteIssue } from '@/shared/hooks/useIssues';
import { InlineStatusSelector } from '@/features/one-on-one/goals-review/components/InlineStatusSelector';
import { isOverdue } from '@/features/one-on-one/goals-review/utils/overdue.utils';
import {
  AlertCircle,
  Check,
  Lightbulb,
  Repeat,
  Trash2,
  Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { UpdateTaskModal } from '@/features/goals/goal-form/UpdateTaskModal';
import { Button } from '@/shared/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/shared/components/ui/Dialog';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { TaskOccurrenceHistory } from '@/features/goals/goal-view/components/TaskOccurrenceHistory';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui';

function getNextOccurrenceDate(
  config: RecurrenceConfigUnion,
  currentStartDate: string
): Date {
  const start = new Date(currentStartDate);
  const next = new Date(start);

  switch (config.pattern) {
    case RecurrencePattern.DAILY:
      next.setUTCDate(next.getUTCDate() + 1);
      break;
    case RecurrencePattern.WEEKLY:
      next.setUTCDate(next.getUTCDate() + 7);
      break;
    case RecurrencePattern.MONTHLY: {
      const dayOfMonth = config.dayOfMonth ?? next.getUTCDate();
      // Set day to 1 first to prevent overflow when advancing month
      next.setUTCDate(1);
      next.setUTCMonth(next.getUTCMonth() + 1);
      // Clamp to the last day of the target month
      const daysInMonth = new Date(
        Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)
      ).getUTCDate();
      next.setUTCDate(Math.min(dayOfMonth, daysInMonth));
      break;
    }
    case RecurrencePattern.CUSTOM_DAYS:
      if (config.intervalDays)
        next.setUTCDate(next.getUTCDate() + config.intervalDays);
      break;
  }

  return next;
}

function getRecurrenceBadgeKey(
  config: RecurrenceConfigUnion
): 'badgeDaily' | 'badgeWeekly' | 'badgeMonthly' | 'badgeCustom' {
  switch (config.pattern) {
    case RecurrencePattern.DAILY:
      return 'badgeDaily';
    case RecurrencePattern.WEEKLY:
      return 'badgeWeekly';
    case RecurrencePattern.MONTHLY:
      return 'badgeMonthly';
    case RecurrencePattern.CUSTOM_DAYS:
    default:
      return 'badgeCustom';
  }
}

interface TaskRowProps {
  task: TaskIssue;
  onStatusChange?: (newStatus: IssueStatus) => Promise<void>;
  onView?: () => void;
  assignees?: IssueAssignee[];
  currentEmployeeId?: string;
}

export const TaskRow: React.FC<TaskRowProps> = ({
  task,
  onStatusChange,
  onView,
  assignees,
  currentEmployeeId,
}) => {
  const { t } = useTranslation('oneOnOne');
  const { t: tGoals } = useTranslation('goals');
  const currentEmployee = useEmployeeContext();
  const { formatDate } = useDateFormatter();
  const updateTaskStatusMutation = useUpdateTaskStatus();
  const deleteIssueMutation = useDeleteIssue();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);
  const taskIsOverdue = isOverdue(task);

  const isManager = task.creator === currentEmployee.workInfo.managerId;
  const isRepeatableTask = !!task.recurrence;

  const isCreator = task.creator === currentEmployeeId;
  const isAssignee =
    task.assignees?.some((assigneeId) => assigneeId === currentEmployeeId) ??
    false;
  const canEdit = isCreator || isAssignee || isManager;

  const relatedSignalsCount = task.relatedSignals?.length ?? 0;
  const hasRelatedSignals = relatedSignalsCount > 0;

  const formatTaskDate = (dateString?: string) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return formatDate(date, 'short');
    } catch {
      return '—';
    }
  };

  const getNextDateLabel = (): string | null => {
    if (!task.recurrence?.current || !task.recurrence?.config) return null;
    const config = task.recurrence.config as RecurrenceConfigUnion;
    const nextDate = getNextOccurrenceDate(
      config,
      task.recurrence.current.startDate
    );
    return formatDate(nextDate, 'short');
  };

  const showRecurringCompletionToast = () => {
    const nextLabel = getNextDateLabel();
    if (!nextLabel) return;
    const title = String(tGoals('occurrenceCompleted'));
    const descriptionFn = tGoals('occurrenceCompletedDescription') as (
      d: string
    ) => string;
    toast.success(title, { description: descriptionFn(nextLabel) });
  };

  const handleStatusChange = async (newStatus: IssueStatus) => {
    if (onStatusChange) {
      try {
        await onStatusChange(newStatus);
        toast.success(t('taskStatusUpdated'), {
          description: t('taskStatusUpdatedSuccessfully'),
        });
        if (newStatus === IssueStatus.COMPLETED && isRepeatableTask) {
          showRecurringCompletionToast();
        }
      } catch (error) {
        toast.error(t('failedToUpdateTaskStatus'), {
          description:
            error instanceof Error
              ? error.message
              : t('couldNotUpdateTaskStatus'),
        });
      }
    } else {
      // Default behavior: use the mutation hook
      try {
        await updateTaskStatusMutation.mutateAsync({
          id: task._id,
          status: newStatus,
        });
        toast.success(t('taskStatusUpdated'), {
          description: t('taskStatusUpdatedSuccessfully'),
        });
        if (newStatus === IssueStatus.COMPLETED && isRepeatableTask) {
          showRecurringCompletionToast();
        }
      } catch (error) {
        toast.error(t('failedToUpdateTaskStatus'), {
          description:
            error instanceof Error
              ? error.message
              : t('couldNotUpdateTaskStatus'),
        });
      }
    }
  };

  const handleDelete = async () => {
    try {
      await deleteIssueMutation.mutateAsync(task._id);
      setShowDeleteDialog(false);
      toast.success(t('taskDeleted'), {
        description: t('taskDeletedSuccessfully'),
      });
    } catch (error) {
      toast.error(t('failedToDeleteTask'), {
        description:
          error instanceof Error ? error.message : t('couldNotDeleteTask'),
      });
    }
  };

  return (
    <div
      className={cn(
        'group transition-colors duration-150',
        taskIsOverdue
          ? 'border-s-2 border-b-0 border-red-300 bg-red-50/30'
          : 'hover:bg-gray-50',
        onView && 'cursor-pointer'
      )}
    >
      <div
        className="grid grid-cols-12 gap-3 items-center px-4 py-3"
        onClick={onView}
      >
        {/* Status Column */}
        <div
          className="col-span-2 flex justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          <InlineStatusSelector
            value={task.status}
            onValueChange={handleStatusChange}
            disabled={updateTaskStatusMutation.isPending || !canEdit}
          />
        </div>

        {/* Task Title Column */}
        <div className="col-span-4">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h4
                  className={cn(
                    'text-sm font-medium leading-snug',
                    task.status === IssueStatus.COMPLETED
                      ? 'text-gray-500 line-through'
                      : taskIsOverdue
                      ? 'text-red-800'
                      : 'text-gray-800'
                  )}
                >
                  {task.title}
                </h4>
                {isRepeatableTask && task.recurrence?.config && (
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-primary/10 text-primary text-[0.625rem] font-medium whitespace-nowrap hover:bg-primary/20 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHistoryDrawer(true);
                    }}
                  >
                    <Repeat className="h-3 w-3" />
                    {
                      tGoals(
                        getRecurrenceBadgeKey(
                          task.recurrence.config as RecurrenceConfigUnion
                        )
                      ) as string
                    }
                  </button>
                )}
              </div>
              {task.description && (
                <p
                  className={cn(
                    'text-xs mt-0.5 line-clamp-1',
                    taskIsOverdue ? 'text-red-600' : 'text-gray-500'
                  )}
                >
                  {task.description}
                </p>
              )}
              {isRepeatableTask &&
                task.recurrence?.current &&
                (task.status !== IssueStatus.COMPLETED ||
                  task.recurrence.current.isCompleted) && (
                  <div className="flex items-center gap-2 mt-1">
                    {task.recurrence.current.isCompleted ? (
                      <>
                        <span className="inline-flex items-center gap-0.5 text-[0.65rem] text-emerald-600">
                          <Check className="h-2.5 w-2.5" />
                          {tGoals('currentDone') as string}
                        </span>
                        {getNextDateLabel() && (
                          <>
                            <span className="text-gray-300">·</span>
                            <span className="text-[0.65rem] text-muted-foreground">
                              {(tGoals('nextLabel') as (d: string) => string)(
                                getNextDateLabel() as string
                              )}
                            </span>
                          </>
                        )}
                      </>
                    ) : (
                      getNextDateLabel() && (
                        <span className="text-[0.65rem] text-muted-foreground">
                          {(tGoals('dueLabel') as (d: string) => string)(
                            getNextDateLabel() as string
                          )}
                        </span>
                      )
                    )}
                  </div>
                )}
            </div>
            {hasRelatedSignals && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex shrink-0 self-center items-center gap-1 text-yellow-500">
                      <span className="text-xs font-medium text-muted-foreground">
                        {relatedSignalsCount}
                      </span>
                      <span className="text-xs font-medium text-muted-foreground">
                        ×
                      </span>
                      <Lightbulb className="h-5 w-5" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="font-normal">
                    {(t('relatedSignalsTooltip') as (count: number) => string)(
                      relatedSignalsCount
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>

        {/* Assignees Column */}
        <div className="col-span-2 flex justify-center">
          <TaskAssignees assignees={assignees ?? []} />
        </div>

        {/* Start Date Column */}
        <div className="col-span-1 flex justify-center">
          <span
            className={cn(
              'text-xs',
              taskIsOverdue ? 'text-red-600' : 'text-gray-600'
            )}
          >
            {formatTaskDate(task.startDate)}
          </span>
        </div>

        {/* Due Date Column */}
        <div className="col-span-1 flex justify-center items-center">
          <span
            className={cn(
              'inline-flex items-center gap-1 text-xs',
              taskIsOverdue ? 'text-red-700 font-semibold' : 'text-gray-600'
            )}
          >
            {taskIsOverdue && <AlertCircle className="h-3 w-3 text-red-600" />}
            {formatTaskDate(task.dueDate)}
          </span>
        </div>

        {/* Finish Date Column */}
        <div className="col-span-1 flex justify-center items-center">
          <span className="text-xs text-gray-600">
            {formatTaskDate(
              task.metadata?.closedAt instanceof Date
                ? task.metadata.closedAt.toISOString()
                : task.metadata?.closedAt
            )}
          </span>
        </div>

        <div
          className="col-span-1 flex justify-center items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {isRepeatableTask && (
            <Button
              variant="ghost"
              onClick={() => setShowHistoryDrawer(true)}
              className="h-8 w-8 p-0 text-primary/60 hover:text-primary hover:bg-primary/10"
            >
              <Clock className="h-4 w-4" />
            </Button>
          )}
          {canEdit && <UpdateTaskModal task={task} />}
          {(isCreator || isManager) && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteDialog(true)}
                className="h-8 w-8 p-0 text-gray-600 hover:text-red-600 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
              <Dialog
                open={showDeleteDialog}
                onOpenChange={setShowDeleteDialog}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('deleteTaskTitle')}</DialogTitle>
                    <DialogDescription>
                      {t('deleteTaskDescription')(task.title)}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setShowDeleteDialog(false)}
                      disabled={deleteIssueMutation.isPending}
                    >
                      {t('deleteTaskCancel')}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleDelete}
                      disabled={deleteIssueMutation.isPending}
                    >
                      {deleteIssueMutation.isPending
                        ? t('deleteTaskDeleting')
                        : t('deleteTaskConfirm')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* History Drawer */}
      {isRepeatableTask && (
        <TaskOccurrenceHistory
          task={task}
          isOpen={showHistoryDrawer}
          onClose={() => setShowHistoryDrawer(false)}
          canEdit={canEdit}
        />
      )}
    </div>
  );
};
